import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import {
    CSSProperties,
    ComponentProps,
    ReactEventHandler,
    ReactNode,
    useRef,
    useState,
} from 'react';

import useMeasure from '../../hooks/useMeasure';
import borderRadiusAsClasses from '../../shared/borderRadiusAsClasses';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Vignette from '../Vignette/Vignette';

import styles from './Figure.module.css';

type FigureProps = {
    src: string;
    alt?: string;
    caption?: ReactNode;
    vignette?: ComponentProps<typeof Vignette>['type'];
    borderRadius?: 'all' | 'top' | 'bottom' | 'none' | 'left' | 'right';
    borderRadiusSize?: 'regular' | 'large';
    defaultPlaceholderRatio?: number;
    captionPosition?: 'below' | 'inside' | 'inside-no-padding';
    saturation?: number;
};

const Figure = ({
    src,
    caption,
    alt,
    vignette,
    borderRadius = 'none',
    borderRadiusSize = 'regular',
    defaultPlaceholderRatio = 1 / 2,
    captionPosition = 'below',
    saturation = 1,
    ...imageProps
}: FigureProps) => {
    const [lastLoadedSrc, setLastLoadedSrc] = useState<string>('');
    const imageRef = useRef<HTMLImageElement>(null);
    const measure = useMeasure(imageRef);
    const placeholderRatio = measure.height
        ? measure.height / measure.width
        : defaultPlaceholderRatio;

    const hasLoaded = src && lastLoadedSrc.endsWith(src);

    const handleImageLoaded: ReactEventHandler<HTMLImageElement> = (event) => {
        setLastLoadedSrc(event.currentTarget.src);
    };

    const borderRadiusClassName = borderRadiusAsClasses(borderRadius, {
        size: borderRadiusSize,
    });

    const imageWrapperClasses = classNames(
        styles.ImageWrapper,
        styles.Stretch,
        borderRadiusClassName
    );

    const placeholderClasses = classNames(
        styles.Placeholder,
        styles.Stretch,
        borderRadiusClassName
    );

    const captionClasses = classNames(styles.Caption, {
        [styles.Caption_below]: captionPosition === 'below',
        [styles.Caption_inside]: captionPosition === 'inside',
        [styles.Caption_insideNoPadding]:
            captionPosition === 'inside-no-padding',
    });

    const imageStyles: CSSProperties = {
        filter: saturation !== 1 ? `saturate(${saturation})` : undefined,
    };

    return (
        <figure className={styles.Root}>
            <div className={styles.HiddenLoadingImageWrapper}>
                <img
                    src={src}
                    alt=""
                    className={styles.HiddenLoadingImage}
                    onLoad={handleImageLoaded}
                    ref={imageRef}
                />
            </div>

            <span
                className={styles.PlaceholderWrapper}
                style={{ paddingTop: `${placeholderRatio * 100}%` }}
            >
                <AnimatePresence>
                    {!hasLoaded && (
                        <motion.span
                            className={placeholderClasses}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <LoadingSpinner />
                        </motion.span>
                    )}
                </AnimatePresence>
            </span>

            <span className={imageWrapperClasses}>
                <AnimatePresence>
                    <motion.img
                        {...imageProps}
                        key={lastLoadedSrc}
                        src={lastLoadedSrc}
                        alt={alt}
                        className={styles.Image}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{
                            opacity: 0,
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                        }}
                        transition={{ duration: 0.15 }}
                        style={imageStyles}
                    />
                </AnimatePresence>
            </span>

            {vignette && <Vignette type={vignette} />}

            {caption && (
                <figcaption className={captionClasses}>{caption}</figcaption>
            )}
        </figure>
    );
};

export default Figure;
